import { CommonModule, Location } from '@angular/common';
import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { DropdownModule } from 'primeng/dropdown';
import { firstValueFrom, Observable, Subject } from 'rxjs';

import { ENVIRONMENT, Environment } from '@environment';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { Language } from '@usheru-hff/shared/domain';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';
import { CountrySelectionService } from '@usheru-hff/sdk/country-selection';
import { COUNTRY_VERSION } from '@usheru-hff/sdk/location';
import { Select } from 'primeng/select';

/**
 * Component to change the language of the app updating the {@link TranslationsService}.
 *
 * //TODO barbeitoalex propostal - organice life cycle methods. Split in specific method to improve docs.
 * //TODO barbeitoalex propostal - subscribe to current language to update the translate pipes
 */
@Component({
  selector: 'ush-language-selector',
  templateUrl: './language-selector.component.html',
  styleUrls: ['./language-selector.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, DropdownModule, FormsModule, Select],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LanguageSelectorComponent implements OnInit, OnDestroy {
  private readonly env = inject(ENVIRONMENT);
  private readonly countrySelectionService = inject(CountrySelectionService);
  private readonly initialCountryVersion = this.countrySelectionService.initialCountryVersionCode;

  @ViewChild('dropdown', { read: ElementRef }) dropdownRef: ElementRef<HTMLElement>;

  readonly FAVORITE_LANG_TOKEN = 'favorite_lang';

  /**
   * @docsNotRequired Language
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedLanguage: Language;

  /**
   * Language provided by the {@link TranslationsService}.
   */
  availableLanguages$: Observable<Language[]>;

  /**
   * @docsNotRequired
   */
  useEnglishLanguage = false;

  /**
   * @docsNotRequired
   */
  envTranslationFooter = false;
  envTranslationHeader = false;

  destroy$ = new Subject<void>();

  constructor(
    private translationsService: TranslationsService,
    private applicationRef: ApplicationRef,
    private location: Location,
    private channelService: ChannelService,
    private cookiesService: CookieService
  ) {}

  ngOnInit(): void {
    this.useEnglishLanguage = this.env.languageSelectorEnglishName;
    this.envTranslationFooter = this.env.customPageParams?.translationFooter;
    this.envTranslationHeader = this.env.customPageParams?.translationHeader;

    this.availableLanguages$ = this.channelService.getChannelAvailableLanguages();
    this.selectedLanguage = this.translationsService.currentLanguage();
  }

  updateLanguage(event) {
    const code = event.value.code;
    if (this.cookiesService.check(this.FAVORITE_LANG_TOKEN)) {
      this.cookiesService.delete(this.FAVORITE_LANG_TOKEN);
    }
    this.cookiesService.set(this.FAVORITE_LANG_TOKEN, code, 365, '/');

    if (this.env.countryBasedApplication?.enabled && !this.cookiesService.check(COUNTRY_VERSION)) {
      if (this.initialCountryVersion()) {
        this.cookiesService.set(COUNTRY_VERSION, this.initialCountryVersion(), 365, '/');
      }
    }
    this.navigateToNewLangUrl(code);
  }

  private navigateToNewLangUrl(langCode: string) {
    const baseUrl = this.location.path(true);
    const urlParts = baseUrl.split('/'); // Split by slashes
    urlParts[1] = langCode; // Replace language segment
    this.modifyUrl(langCode);
    this.applicationRef.tick();
    // this.blockScreen();
    window.location.reload();
  }
  private modifyUrl(langCode: string) {
    const currentUrl = window.location.href;
    const newUrl = currentUrl.replace(/\/[a-z]{2}\//, `/${langCode}/`);
    window.history.pushState({}, '', newUrl);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
